import {isEmpty} from 'ramda';

import {useSubscribeToNotificationsQuery} from '@dms/api/appsync';
import {ContextTarget, useLazyGetDocumentContextCountQuery} from '@dms/api/core';

const FILE_UPLOADED_NOTIFICATION_TYPE = 'file_storage-file_uploaded';
const FILE_UPLOAD_SUCCESS_STATUS = 'success';

interface UseRefreshDocumentsCountArgs {
  contextTarget: ContextTarget;
  contextId: string;
}

/**
 * Keeps the document-context count badge in sync after a document is generated
 * or uploaded for an entity.
 *
 * The count is updated on the backend asynchronously (Kafka between metadá and
 * PHP), so refetching immediately after the triggering action reads a stale
 * value. Instead of guessing the propagation delay with a timeout or polling,
 * this subscribes to background notifications and refetches the count as soon
 * as a `file_storage-file_uploaded` success event arrives. The forced refetch
 * shares the badge's RTK Query cache entry, so any subscribed component updates
 * automatically.
 *
 * The returned action triggers an immediate refetch for callers that want a
 * best-effort update right after the action itself.
 */
export function useRefreshDocumentsCount(args: UseRefreshDocumentsCountArgs) {
  const [getDocumentsCount] = useLazyGetDocumentContextCountQuery();

  const refreshDocumentsCount = () => {
    if (isEmpty(args.contextId)) {
      return;
    }

    getDocumentsCount({contextTarget: args.contextTarget, contextId: args.contextId}, false);
  };

  useSubscribeToNotificationsQuery((notification) => {
    if (notification.type !== FILE_UPLOADED_NOTIFICATION_TYPE) {
      return;
    }

    if (notification.payload.fileStatus !== FILE_UPLOAD_SUCCESS_STATUS) {
      return;
    }

    refreshDocumentsCount();
  });

  return refreshDocumentsCount;
}
